import React, { useState } from "react";
import { Contact } from "@akfdev-com/shared";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

interface NewRecordMenuProps {
  contact: Contact;
  ownerId?: string;
}

/**
 * Type-aware "+ New ▾" menu
 *
 * Renders a dropdown menu of financial record creation actions that adapt to the contact type:
 * - Resident → New Invoice · New Service Charge · Record Payment
 * - Supplier → New Bill · Record Payment
 * - Customer → New Invoice · Record Payment
 *
 * Each action deep-links to the financial-records create form with `contactId` and `ownerId`
 * pre-populated as non-editable URL parameters.
 *
 * Validates: Requirement 4.1
 */
export const NewRecordMenu: React.FC<NewRecordMenuProps> = ({
  contact,
  ownerId = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Menu items by contact type
  const menuItems: Array<{ label: string; recordType: string }> = (() => {
    switch (contact.type) {
      case "resident":
        return [
          { label: "New Invoice", recordType: "invoice" },
          { label: "New Service Charge", recordType: "service_charge" },
          { label: "Record Payment", recordType: "payment" },
        ];
      case "supplier":
        return [
          { label: "New Bill", recordType: "bill" },
          { label: "Record Payment", recordType: "payment" },
        ];
      case "customer":
        return [
          { label: "New Invoice", recordType: "invoice" },
          { label: "Record Payment", recordType: "payment" },
        ];
      default:
        return [];
    }
  })();

  const buildCreateUrl = (recordType: string) => {
    const params = new URLSearchParams({
      type: recordType,
      contactId: contact.id,
      ...(ownerId && { ownerId }),
    });
    return `/dashboard/financial-records/new?${params.toString()}`;
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors"
        style={{
          backgroundColor: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1d4ed8",
        }}
      >
        + New
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full mt-1 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-48">
          {menuItems.map((item, idx) => (
            <a
              key={idx}
              href={buildCreateUrl(item.recordType)}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors first:rounded-t-md last:rounded-b-md"
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
